import React, { useState } from 'react';
import Api from 'api';
import ReviewInfo from 'components/ReviewInfo';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import Tabs from 'components/UI/Tabs';
import Loader from 'components/UI/Loader';
import Link from 'components/UI/Link';
import { getCountryCode, useLang, useTrans } from 'utils/localisation';
import { useDefferedCallback } from 'utils/hooks';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './Testimonials.module.scss';

function Testimonials({ title, description, pageKey }) {
  const lang = useLang();
  const t = useTrans(['common', 'testimonials']);
  const [opacity, setOpacity] = useState({
    '--opacity': 1,
  });
  const [active, setActive] = useState(0);
  const [counterCurrent, setCounterCurrent] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [tabNames, setTabNames] = useState([]);
  const [total, setTotal] = useState(0);
  const tabsAriaLabel = 'Content Tabs';
  const shouldShowTabsCounter = lang !== 'ae' && reviews?.length > 0;
  const testimonialsLink =
    lang === 'ae' || lang === 'az'
      ? 'https://andersenlab.com/company/testimonials'
      : '/company/testimonials';

  const loadData = async () => {
    const headers = { 'X-Country-Code': getCountryCode()?.toUpperCase() ?? 'EN' };

    await Api.getReviews(pageKey, lang, headers)
      .then(res => {
        setReviews(res.data);
        setTotal(res.total);
        setTabNames(
          res.data.map(item => {
            return {
              id: item.id,
              tabLogo: item.company_logo,
            };
          }),
        );
        setActive(res.data[0].id);
      })
      .finally(() => setLoading(false));
  };

  const { inViewRef } = useDefferedCallback(loadData, {
    rootMargin: pageKey !== '/case-studies' ? '100% 0% 100% 0%' : '0px',
  });

  return (
    <Container id="testimonials">
      <div className={styles.wrapper} ref={inViewRef}>
        <header className={styles.header}>
          <Title tag="p" size="h2">
            {title ?? t('title')}
          </Title>
          {shouldShowTabsCounter && (
            <div className={styles.counter}>{`${counterCurrent}/${reviews?.length}`}</div>
          )}
        </header>
        <Subtitle size="lg">{description}</Subtitle>
        <div className={styles.content}>
          {loading && <Loader theme="fullHeight" />}
          {reviews?.length > 0 && (
            <>
              <Tabs
                className={styles.tabButtons}
                filters={tabNames}
                active={active}
                setActive={setActive}
                setStyles={setOpacity}
                setActiveTabNumber={setCounterCurrent}
                tabsAriaLabel={tabsAriaLabel}
              />
              {reviews?.map(item => (
                <section
                  key={item.id}
                  id={item.id}
                  className={styles.sections}
                  style={opacity}
                  role="tabpanel"
                  tabIndex={0}
                  aria-labelledby={item.id}
                  hidden={active !== item.id}
                >
                  <ReviewInfo {...item} />
                </section>
              ))}
              <div className={styles.footer}>
                <Link className={styles.link} href={testimonialsLink}>
                  {t('allTestimonials')} {lang !== 'ae' ? `(${total})` : ''}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </Container>
  );
}

Testimonials.displayName = 'Testimonials';

export default withErrorBoundary(Testimonials);
